'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { useCompany } from '@/lib/data/company';
import { isGuestPath } from '@/lib/auth/guest-paths';
import {
  isDayFirst,
  isTwoDigitYear,
  resolveDateFormat,
  type DateDisplayFormat,
} from '@/lib/utils/date-format';
import { storedCalendarDate } from '@/lib/utils/date';

/**
 * One date format for the whole app, taken from the tenant's own
 * `company.dateFormat` setting.
 *
 * The setting is stored trimmed (`dd/mm/yy` | `mm/dd/yy`) and resolved by
 * `resolveDateFormat`, which the reports already use — this is the same
 * contract reaching table cells and detail modals, so one date never reads two
 * ways on two screens.
 *
 * Delivered by CONTEXT, not by calling `useCompany()` wherever a date is
 * rendered. The shared `Table` formats date cells, and a query inside it would
 * make every table in the app — including the ones in the developer handbook
 * and in tests — require a QueryClient and a stubbed `/company`. The provider
 * runs that query ONCE in the app shell; everything below reads a plain value,
 * and anything mounted outside the shell falls back to the default rather than
 * throwing.
 *
 * UTC for a value at exactly 00:00:00.000Z. These are CALENDAR dates the
 * backend serializes at UTC midnight, not instants: formatting them in the
 * viewer's local zone shifts them back a day for every viewer west of UTC (a
 * 1 September go-live showing as 31 August in New York).
 *
 * Local for any other value (MTFM-646). Meteor wrote the same fields as the
 * author's LOCAL midnight converted to UTC — `2026-04-27T15:55:10.333Z` is
 * 01:55 on the 28th in Sydney — and displays them in the viewer's zone. Reading
 * those in UTC showed every Meteor-written date a day early east of UTC. The
 * rule is `storedCalendarDate` in `@/lib/utils/date`.
 */

export type { DateDisplayFormat };

const DEFAULT_FORMAT: DateDisplayFormat = 'DD/MM/YYYY';

const DateFormatContext = createContext<DateDisplayFormat>(DEFAULT_FORMAT);

/**
 * Pure formatter, for anywhere a hook cannot go (a cell-type dispatcher, a
 * test, a non-component helper).
 *
 * Returns `''` for null, an empty string, or a value that does not parse — a
 * malformed date degrades to an empty cell rather than throwing and taking the
 * whole row down with it.
 */
export function formatDateWith(
  value: string | null | undefined,
  format: DateDisplayFormat,
): string {
  if (!value) return '';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';

  // UTC-midnight `Date` naming the calendar day the viewer should see: a
  // 00:00Z value unchanged, a Meteor local-midnight value on its local day.
  const date = storedCalendarDate(parsed);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const full = String(date.getUTCFullYear());
  const year = isTwoDigitYear(format) ? full.slice(-2) : full;

  return isDayFirst(format) ? `${day}/${month}/${year}` : `${month}/${day}/${year}`;
}

/**
 * A real INSTANT rendered as a date alone, in the viewer's zone.
 *
 * The third case, and the reason all three exist. `formatDateWith` reads in UTC
 * because a calendar date is stored at UTC midnight and would otherwise slip a
 * day; that is exactly wrong for a timestamp. A login recorded at 07:52 in
 * Manila is 23:52 the PREVIOUS day in UTC, so formatting it as a calendar date
 * showed Last login one day early for every user east of UTC — which is what
 * was reported. v1 formats the same field with `moment(...)`, which is local,
 * so it never had the problem.
 *
 * Date only, no time: v1's users table shows just the date
 * (`UserPanel.jsx:190-192`), and the column is about "when did they last sign
 * in", not the minute.
 */
export function formatInstantDateWith(
  value: string | null | undefined,
  format: DateDisplayFormat,
): string {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const full = String(date.getFullYear());
  const year = isTwoDigitYear(format) ? full.slice(-2) : full;

  return isDayFirst(format) ? `${day}/${month}/${year}` : `${month}/${day}/${year}`;
}

/**
 * Same, for a value that is a real INSTANT rather than a calendar date — an
 * upload time, an audit timestamp — rendered as `<date> h:mm a`.
 *
 * Local zone, deliberately, and this is the one place that differs from
 * `formatDateWith` above. A calendar date is serialized at UTC midnight and
 * has to be read back in UTC or it slips a day; a timestamp carries a real
 * time of day, and showing a 9am Sydney upload as the previous day at 23:00
 * would be wrong in exactly the way UTC protects the other case from. v1
 * agrees: its `AppDatePipe` hands the value to Angular's `DatePipe`, which
 * formats in the viewer's zone.
 *
 * v1 parity: `changeplan-frontend`'s `DateFormatService.format(value, true)`
 * appends ` h:mm a` to the tenant's own format — appended OUTSIDE the format
 * string's `m -> M` normalization there, so the minutes token never becomes a
 * month. Here the order/width come from `DateDisplayFormat`, so there is no
 * token string to normalize and no such trap.
 */
export function formatDateTimeWith(
  value: string | null | undefined,
  format: DateDisplayFormat,
): string {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const full = String(date.getFullYear());
  const year = isTwoDigitYear(format) ? full.slice(-2) : full;
  const datePart = isDayFirst(format) ? `${day}/${month}/${year}` : `${month}/${day}/${year}`;

  const hours24 = date.getHours();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const meridiem = hours24 >= 12 ? 'PM' : 'AM';

  return `${datePart} ${hours12}:${minutes} ${meridiem}`;
}

/** Reads the company's setting once and publishes it to the tree below. */
export function CompanyDateFormatProvider({ children }: Readonly<{ children: ReactNode }>) {
  // Mounted in the root shell, so it runs on guest routes too — where there is
  // no session and `/company` 401s. `clientApi` answers a 401 by sending the
  // browser to /login, so leaving this ungated bounced anyone who opened
  // /forgot-password or /sso straight back to the sign-in screen before they
  // could type anything. Same gate the sibling shell providers already use.
  const pathname = usePathname();
  const { data } = useCompany({ enabled: !isGuestPath(pathname) });
  const format = resolveDateFormat(data?.company?.dateFormat);

  return <DateFormatContext.Provider value={format}>{children}</DateFormatContext.Provider>;
}

/** The tenant's display format, or DD/MM/YYYY outside the provider. */
export function useDateFormat(): DateDisplayFormat {
  return useContext(DateFormatContext);
}

/**
 * The formatter bound to the tenant's setting. Memoized on the format alone,
 * so passing it into a `renderCell` closure does not defeat memoization on
 * every render.
 */
export function useFormatDate(): (value: string | null | undefined) => string {
  const format = useDateFormat();
  return useMemo(
    () => (value: string | null | undefined) => formatDateWith(value, format),
    [format],
  );
}

/** `useFormatDate`'s counterpart for an instant shown as a date alone. */
export function useFormatInstantDate(): (value: string | null | undefined) => string {
  const format = useDateFormat();
  return useMemo(
    () => (value: string | null | undefined) => formatInstantDateWith(value, format),
    [format],
  );
}

/** `useFormatDate`'s counterpart for instants — see `formatDateTimeWith`. */
export function useFormatDateTime(): (value: string | null | undefined) => string {
  const format = useDateFormat();
  return useMemo(
    () => (value: string | null | undefined) => formatDateTimeWith(value, format),
    [format],
  );
}
